import React, { useState, useEffect } from "react";
import { Box } from "@mui/system";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import TableHead from "@mui/material/TableHead";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

import secureLocalStorage from "react-secure-storage";
import SearchBar from "../../common/components/search/SearchBar";
import VisibilityIcon from "@mui/icons-material/Visibility";

import Banner from "../../common/components/banner";
import banner from "../../assets/images/banners/competence_banner.png";

import { useDefaultStyles } from "../../common/styles/default";

import { useModal } from "../../common/hooks/useModal";
import CompetenceService from "../../services/CompetenceService";
import ICompetenceList from "../../types/Competence";

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number
  ) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

export default function List() {
  const navigate = useNavigate();
  const theme = useTheme(); // Usando o hook useTheme para acessar os breakpoints
  const { t } = useTranslation();
  const styles = useDefaultStyles();

  const { handleOpenModal, dataModal } = useModal();

  const handleAdd = () => {
    handleOpenModal({
      displayName: "CompetenceModal",
      data: { checklist: 0, loadCompetences: loadCompetences },
    });
  };

  const paginate = 1;
  const firstpage = 1;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const [competences, setCompetences] = useState<ICompetenceList>();

  const [competenceRemove, setCompetenceRemove] = useState("");

  const [open, setOpen] = useState(false);

  const localPermissions = secureLocalStorage
    .getItem("permissions")
    ?.toString();
  const permissions = localPermissions?.split(",");

  const handleClickOpen = (id: any) => {
    setCompetenceRemove(id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
    const npage = newPage + 1;

    setPage(newPage);
    loadCompetences(npage, paginate);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {};

  const loadCompetences = async (page: number, paginate: number, query?: string) => {
    await CompetenceService.getAll(page, paginate, query)
      .then((response: any) => {
        if (response) {
          setCompetences(response.data);
        }
      })
      .catch((e: any) => {
        toast(e?.response?.data?.Msg ?? e?.message, { type: "error" });
      });
  };

  const handleSearch = (payload: string | { q?: string; filters?: Record<string, any> }) => {
    if (!payload) {
      loadCompetences(firstpage, paginate);
      return;
    }

    if (typeof payload === "string") {
      const q = payload.trim();
      if (!q) {
        loadCompetences(firstpage, paginate);
        return;
      }
      const query = `filter=${encodeURIComponent(q)}`;
      loadCompetences(firstpage, paginate, query);
      return;
    }

    const { q, filters } = payload;
    const parts: string[] = [];
    if (q && q.trim() !== "") parts.push(`filter=${encodeURIComponent(q.trim())}`);
    if (filters) {
      Object.keys(filters).forEach((k) => {
        const v = filters[k];
        if (v !== undefined && v !== null && `${v}`.toString() !== "") {
          parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
        }
      });
    }

    if (parts.length === 0) {
      loadCompetences(firstpage, paginate);
      return;
    }

    const query = parts.join("&");
    loadCompetences(firstpage, paginate, query);
  };

  const editCompetence = (id: number) => {
    handleOpenModal({
      displayName: "CompetenceModal",
      data: {
        idCompetence: id,
        checklist: 0,
        loadCompetences: loadCompetences,
      },
    });
  };

  const openExtraCompetence = (id: number) => {
    handleOpenModal({
      displayName: "CompetenceExtraModal",
      data: {
        idCompetence: id,
        showBtSave: 0,
      },
    });
  };

  const removeCompetence = async () => {
    try {
      const resp = await CompetenceService.remove(competenceRemove);
      setCompetenceRemove("");
      loadCompetences(firstpage, paginate);
      toast(resp?.data?.Msg, {
        type: "success",
      });
      setOpen(false);
    } catch (e: any) {
      setCompetenceRemove("");
      loadCompetences(firstpage, paginate);
      setOpen(false);
      toast(
        e?.response?.data?.Msg ??
          e?.message ??
          t("toast_list_competence_description"),
        {
          type: "error",
        }
      );
    }
    // CompetenceService.remove(competenceRemove)
    //   .then((response: any) => {
    //     setCompetenceRemove("");
    //     loadCompetences(firstpage, paginate);
    //     setOpen(false);
    //   })
    //   .catch((e: Error) => {});
  };

  useEffect(() => {
    loadCompetences(firstpage, paginate);
  }, []);

  return (
    <Box>
      <Banner
        type="medium"
        image={banner}
        title={t("banner_title_competence")}
        subtitle={t("banner_subtitle_competence")}
      />
      <Box
        sx={{
          width: "100%",
          position: "relative",
          paddingLeft: "150px",
          paddingRight: "150px",
          paddingBottom: "100px",

          [theme.breakpoints.down("sm")]: {
            //border: '1px solid black',
            position: "relative",
            ml: "25px",
            width: "94%",
            paddingLeft: "5px",
            paddingRight: "30px",
          },

          [theme.breakpoints.between("sm", "md")]: {
            //border: '1px solid black',
            position: "relative",
            ml: "25px",
            width: "94%",
            paddingLeft: "53px",
            paddingRight: "53px",
          },

          [theme.breakpoints.between("md", "lg")]: {
            //border: '1px solid black',
            position: "relative",
            ml: "25px",
            width: "92%",
            paddingLeft: "34px",
            paddingRight: "30px",
          },

          [theme.breakpoints.between("lg", "xl")]: {
            //border: '1px solid black',
            position: "relative",
            ml: "25px",
            width: "96%",
            paddingLeft: "65px",
            paddingRight: "75px",
          },
        }}
        className={styles.containerForm}
      >
        {permissions?.includes("create_theme") && (
          <Box sx={{ mb: 2, width: "10%" }}>
            <Button variant="contained" onClick={handleAdd}>
              {t("btn_add")}
            </Button>
          </Box>
        )}
        <Box sx={{ mb: 2 }}>
          <SearchBar
            placeholderKey="table_competence_label_name"
            trigger="enter"
            showClear={true}
            onSearch={handleSearch}
            // fixedFilters={{ entidade_nome: secureLocalStorage.getItem("entity_name") || secureLocalStorage.getItem("entity") || "" }}
          />
        </Box>
        <TableContainer component={Paper} sx={{ mt: 5 }}>
          <Table sx={{ minWidth: 600 }} aria-label="custom pagination table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", fontSize: 20 }}>
                  {t("table_competence_label_name")}
                </TableCell>

                <TableCell sx={{ fontWeight: "bold", fontSize: 20 }}>
                  {t("table_label_button")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {competences?.data.map((row) => (
                <TableRow key={row.id}>
                  <TableCell component="th" scope="row">
                    {row.name}
                  </TableCell>
                  <TableCell>
                    {permissions?.includes("update_theme") && (
                      <EditIcon
                        sx={{ cursor: "pointer" }}
                        onClick={() => editCompetence(row.id)}
                      />
                    )}
                    {permissions?.includes("delete_theme") && (
                      <DeleteIcon
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleClickOpen(row.id)}
                      />
                    )}
                    <VisibilityIcon
                      sx={{ cursor: "pointer" }}
                      onClick={() => openExtraCompetence(row.id)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            {competences && competences?.total > rowsPerPage && (
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[]}
                    colSpan={7}
                    count={competences?.total}
                    rowsPerPage={rowsPerPage}
                    labelRowsPerPage={t("table_label_number_per_page")}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t("title_dialog_delete_competence")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t("description_dialog_delete_competence")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t("btn_dialog_no")}</Button>
          <Button onClick={removeCompetence} autoFocus>
            {t("btn_dialog_yes")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
